/**
 * Offline storage, on IndexedDB.
 *
 * Not `localStorage`: a subject pack is hundreds of questions and localStorage
 * is a synchronous ~5 MB store that blocks the main thread. IndexedDB is
 * asynchronous and sized for this.
 *
 * Two stores with different jobs:
 *  - `packs` holds downloaded questions, so practice works with no network;
 *  - `pending` holds answers given while offline, so nothing a student does on
 *    a bus is lost. That queue is the part that actually matters — a lost pack
 *    is an inconvenience, a lost session is work they did for nothing.
 *
 * Packs are per exam profile: Mathematics for JAMB and Mathematics for WAEC
 * are different question sets, so a student sitting both keeps one of each.
 * Packs downloaded before profiles sit in the old `packs` store, keyed by
 * subject alone; they still open, for whichever exam asks, until that exam
 * downloads its own copy.
 */
import { activeExam, type ProfileExam } from "./activeExam";

const DB_NAME = "prepai-offline";
const DB_VERSION = 2;
const LEGACY_PACKS = "packs";
const PACKS = "examPacks";
const PENDING = "pending";

export interface OfflinePack {
  subjectId: string;
  /** The profile it was downloaded for. Null on packs from before profiles. */
  exam?: ProfileExam | null;
  subjectName: string;
  questions: unknown[];
  downloadedAt: number;
  /** Rough size, for the "184 MB used" line. */
  bytes: number;
}

export interface PendingSubmission {
  /** The idempotency key — the same value on every retry. */
  id: string;
  /**
   * The server session, when there was one. Null for a sitting started from a
   * downloaded pack: nothing was opened server-side, so the session is created
   * from the answers on replay.
   */
  sessionId: string | null;
  /** Required when `sessionId` is null — the replay needs to know the subject. */
  subjectId?: string | null;
  answers: { questionId: string; answer: string | null }[];
  queuedAt: number;
  /**
   * Who queued it — the user id from the token at the time.
   *
   * Phones are shared in families. Without this, the next student to sign in
   * on the same phone uploaded the previous student's answers into their own
   * account. Absent on items queued before this existed; those are dropped.
   */
  ownerId?: string | null;
  /** The exam profile it was practised in, so it is scored there. */
  exam?: ProfileExam | null;
}

interface StoredPack extends OfflinePack {
  key: string;
}

function packKey(exam: ProfileExam | null, subjectId: string): string {
  return `${exam ?? "_"}:${subjectId}`;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LEGACY_PACKS))
        db.createObjectStore(LEGACY_PACKS, { keyPath: "subjectId" });
      if (!db.objectStoreNames.contains(PACKS)) db.createObjectStore(PACKS, { keyPath: "key" });
      if (!db.objectStoreNames.contains(PENDING)) db.createObjectStore(PENDING, { keyPath: "id" });
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB unavailable"));
  });
}

async function run<T>(
  store: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(store, mode);
    const request = work(transaction.objectStore(store));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB write failed"));
    transaction.oncomplete = () => db.close();
  });
}

/** True when IndexedDB is usable at all — private mode can refuse it. */
export function offlineSupported(): boolean {
  return typeof indexedDB !== "undefined";
}

function strip({ key: _key, ...pack }: StoredPack): OfflinePack {
  return pack;
}

/**
 * Downloaded packs. Every call is for one exam profile — the active one unless
 * told otherwise — except `everything`, which is what the phone holds.
 */
export const packs = {
  async save(pack: OfflinePack): Promise<void> {
    const exam = pack.exam === undefined ? activeExam() : pack.exam;
    const stored: StoredPack = { ...pack, exam, key: packKey(exam, pack.subjectId) };
    await run(PACKS, "readwrite", (store) => store.put(stored));
    // This exam has its own copy now; the pre-profile one is no longer needed.
    await run(LEGACY_PACKS, "readwrite", (store) => store.delete(pack.subjectId));
  },
  async get(
    subjectId: string,
    exam: ProfileExam | null = activeExam(),
  ): Promise<OfflinePack | undefined> {
    const own = await run<StoredPack | undefined>(PACKS, "readonly", (store) =>
      store.get(packKey(exam, subjectId)),
    );
    if (own) return strip(own);
    return run<OfflinePack | undefined>(LEGACY_PACKS, "readonly", (store) => store.get(subjectId));
  },
  /** This exam's packs, plus any from before profiles it has not replaced. */
  async all(exam: ProfileExam | null = activeExam()): Promise<OfflinePack[]> {
    const mine = (await this.everything()).filter(
      (pack) => (pack.exam ?? null) === exam || pack.exam === null || pack.exam === undefined,
    );
    const own = new Set(mine.filter((p) => (p.exam ?? null) === exam).map((p) => p.subjectId));
    return mine.filter((p) => (p.exam ?? null) === exam || !own.has(p.subjectId));
  },
  /** Every pack on the phone, for every exam — what storage and refreshes need. */
  async everything(): Promise<OfflinePack[]> {
    const [own, legacy] = await Promise.all([
      run<StoredPack[]>(PACKS, "readonly", (store) => store.getAll()),
      run<OfflinePack[]>(LEGACY_PACKS, "readonly", (store) => store.getAll()),
    ]);
    return [...own.map(strip), ...legacy.map((pack) => ({ ...pack, exam: null }))];
  },
  async remove(subjectId: string, exam: ProfileExam | null = activeExam()): Promise<void> {
    await run(PACKS, "readwrite", (store) => store.delete(packKey(exam, subjectId)));
    await run(LEGACY_PACKS, "readwrite", (store) => store.delete(subjectId));
  },
};

export const pending = {
  /**
   * Queue a submission made offline.
   *
   * Keyed on the idempotency key the caller already uses, so queueing the same
   * session twice replaces rather than duplicates — the student is not scored
   * twice for one sitting.
   */
  async add(submission: PendingSubmission): Promise<void> {
    await run(PENDING, "readwrite", (store) => store.put(submission));
  },
  async all(): Promise<PendingSubmission[]> {
    return run<PendingSubmission[]>(PENDING, "readonly", (store) => store.getAll());
  },
  async remove(id: string): Promise<void> {
    await run(PENDING, "readwrite", (store) => store.delete(id));
  },
  async count(): Promise<number> {
    return run<number>(PENDING, "readonly", (store) => store.count());
  },
  /** Everything queued — run on sign-out, after one last attempt to send it. */
  async clear(): Promise<void> {
    await run(PENDING, "readwrite", (store) => store.clear());
  },
};
